import { isPlainObject } from '@spazious/dollhouse-utils';

import { ScopeImpl } from './Scope.js';
import { ScopeEvaluator } from './ScopeEvaluator.js';
import { ScopeExpression } from './ScopeExpression.js';
import { ParentScope, ScopeRecord, ScopeRecordEntries } from './types.js';

export interface EvalScopeOptions {
  include?: string[];
  evaluate?: boolean;
}

const defaultOptions: Required<EvalScopeOptions> = {
  include: [],
  evaluate: true,
};

export class EvalScope<
  TRecord extends ScopeRecord = ScopeRecord,
  TParent extends ParentScope = never
> extends ScopeImpl<TRecord, TParent> {
  evaluator: ScopeEvaluator;
  options: Required<EvalScopeOptions>;
  constructor(record?: TRecord, name?: string, parent?: TParent, opts?: EvalScopeOptions);
  constructor(
    entries?: IterableIterator<ScopeRecordEntries<TRecord>>,
    name?: string,
    parent?: TParent,
    opts?: EvalScopeOptions
  );
  constructor(
    mixed?: TRecord | IterableIterator<ScopeRecordEntries<TRecord>>,
    name?: string,
    parent?: TParent,
    opts?: EvalScopeOptions
  ) {
    super(undefined, name, parent);
    this.evaluator = new ScopeEvaluator(this);
    this.options = { ...defaultOptions, ...opts };
    if (mixed) {
      this.set(mixed);
    }
  }

  get(key: string, bubble = true): any {
    let value = super.get(key, bubble);

    if (value instanceof ScopeExpression) {
      value = this.evaluate(value);
    }

    return value;
  }

  set(entries: IterableIterator<[string, any]>): this;
  set(record: Record<string, any>): this;
  set(key: string, value: any): this;
  set(mixed: string | Record<string, any> | IterableIterator<[string, any]>, value?: any): this {
    this.evaluator.reset();
    return super.set(mixed as any, value);
  }

  evaluate(value: ScopeExpression) {
    return this.evaluator.evaluate(value);
  }

  *entries(evaluate = false): IterableIterator<[string, any]> {
    for (const key of super.keys()) {
      yield [key, evaluate ? this.get(key) : super.get(key)];
    }
  }

  protected parseValue(key: string, value: any) {
    const included = this.options.include.includes(key) ?? true;
    return this.options.evaluate && typeof value === 'string' && included
      ? new ScopeExpression(key, value, this)
      : value;
  }
  /**
   * This function is called when a value is set in the scope and
   * the returned value has to be set as scope.
   */
  protected nestScope(key: string, value: any) {
    return super.nestScope(key, isPlainObject(value) ? new EvalScope(value) : value);
  }

  toJSON(evaluate = false) {
    return Object.fromEntries(this.entries(evaluate));
  }
}
